import type { FichaDeModulo } from "../catalogo"

/**
 * English version of `MODULOS` in `lib/landing/catalogo.ts`.
 *
 * Same entries, same order, same keys; only the visible text and the slug
 * change. If a sheet changes there, it changes here.
 */
export const MODULOS_EN: FichaDeModulo[] = [
  // ── Team ────────────────────────────────────────────────────────────────
  {
    clave: "fichaje",
    slug: "time-tracking",
    nombre: "Time tracking",
    resumen: "Clock-ins, breaks and clock-outs from a phone, a tablet or a computer. The record the law asks for.",
    titular: "The working-time record, done as people walk in.",
    entradilla:
      "Each person clocks in from their phone, a tablet at the entrance or their computer. You get the record the law asks for, everyone’s hours and who is working right now.",
    situaciones: [
      { antes: "The sign-in sheet on the counter", ahora: "The daily record the law asks for, stored", texto: "Every clock-in, break and clock-out, with its time and where it was made." },
      { antes: "“Who’s in today?”", ahora: "Who is working, in real time", texto: "By location, with a warning for anyone doing extra hours." },
      { antes: "Adding up hours at the end of the month", ahora: "Everyone’s hours, already added up", texto: "By day, week and month, and compared with their shift if you use Shifts." },
    ],
    funciones: [
      { titulo: "From the phone", texto: "The Zindra app on iPhone and Android: one button to clock in and another for breaks." },
      { titulo: "On a tablet", texto: "A kiosk at the entrance, with a card or a four-digit code." },
      { titulo: "From the computer", texto: "In Zindra Work, the desktop app, without opening anything else." },
      { titulo: "By location", texto: "Each location has its own area: you clock in while you are there." },
    ],
    conexiones: [
      { modulo: "turnos", texto: "Compares what was clocked with what was planned: late arrivals, extra hours and gaps." },
      { modulo: "nominas", texto: "The month’s hours, to hand for payroll." },
      { modulo: "teletrabajo", texto: "While someone is clocked in remotely, their time and applications." },
      { modulo: "workforce", texto: "What each person did in their hours and their output per hour." },
      { modulo: "informes", texto: "Hours by location and person, next to the day’s sales." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Split shifts and a tablet behind the bar to clock in without taking out a phone." },
      { sector: "servicios", texto: "People clock in from their computer, remotely too." },
      { sector: "ecommerce", texto: "Clocking in by workstation in the warehouse, with output per hour." },
    ],
    preguntas: [
      { pregunta: "Is recording working time mandatory?", respuesta: "In Spain, yes. A daily record of working time has been mandatory since 2019 and must be kept for four years." },
      { pregunta: "Do I need to buy a device?", respuesta: "No. Each person’s phone, any tablet as a kiosk or the computer will do." },
      { pregunta: "Can I start with just Time tracking?", respuesta: "Yes: the core and Time tracking. Shifts, Time off or Payroll can be added later, with the clocked hours already in." },
    ],
  },
  {
    clave: "turnos",
    slug: "shift-scheduling",
    nombre: "Shifts",
    resumen: "Rotas by location and role, published to everyone’s phone.",
    titular: "The weekly rota, without a spreadsheet or a WhatsApp group.",
    entradilla:
      "You build shifts by location and role, Zindra warns you who is missing or overlaps with a holiday, and each person sees theirs on their phone.",
    situaciones: [
      { antes: "The rota spreadsheet, in five versions", ahora: "One rota, the same for everyone", texto: "Published to each person’s phone as soon as you close it." },
      { antes: "Finding out on Monday that someone was on holiday", ahora: "Overlaps flagged while you build it", texto: "With Time off, the rota knows who is away." },
      { antes: "Building the week from scratch", ahora: "Patterns that repeat", texto: "Shift types, patterns and staffing needs per day." },
    ],
    funciones: [
      { titulo: "Rota by location", texto: "Week by week, by role, with who covers each gap." },
      { titulo: "Patterns and shift types", texto: "Morning, afternoon, split: defined once and applied." },
      { titulo: "Preferences and needs", texto: "How many people are needed each day and what each person prefers." },
      { titulo: "AI proposal", texto: "A first rota based on needs and preferences, which you then adjust." },
    ],
    conexiones: [
      { modulo: "ausencias", texto: "Holidays and sick leave appear on the rota and flag overlaps." },
      { modulo: "fichaje", texto: "What was planned against what was clocked." },
      { modulo: "tareas", texto: "Tasks are assigned by shift: opening, closing, cleaning." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Split shifts, extra weekend cover and several venues." },
      { sector: "tienda", texto: "Opening and closing of each shop, with who covers each slot." },
    ],
    preguntas: [
      { pregunta: "Does each person see their shifts?", respuesta: "Yes, in the mobile app, as soon as the rota is published." },
      { pregunta: "Does it work with several locations?", respuesta: "Yes. Each location has its own rota and you can see them together or separately." },
    ],
  },
  {
    clave: "ausencias",
    slug: "time-off",
    nombre: "Time off",
    resumen: "Holidays and leave with their approval, balances and the team calendar.",
    titular: "Holidays requested, approved and on the calendar, without a single email.",
    entradilla:
      "Each person requests their days from their phone, their manager approves them and they go on the team calendar, with everyone’s balance up to date.",
    situaciones: [
      { antes: "Asking for holidays by email", ahora: "Requested and approved in the app", texto: "With a notification to whoever has to approve." },
      { antes: "“How many days do I have left?”", ahora: "Everyone’s balance, in view", texto: "According to the company’s policy." },
      { antes: "Three people off the same week", ahora: "The team calendar, at a glance", texto: "Before approving, you see who else is away." },
    ],
    funciones: [
      { titulo: "Request and approval", texto: "Holidays, personal days and sick leave, each with its flow." },
      { titulo: "Balances and policies", texto: "Days per year, carry-over and types of absence." },
      { titulo: "Team calendar", texto: "Who is away each day, by department." },
      { titulo: "On the phone", texto: "Request and approve from the app." },
    ],
    conexiones: [
      { modulo: "turnos", texto: "Absences appear on the rota and flag overlaps." },
      { modulo: "fichaje", texto: "A day of holiday does not count as a missed clock-in." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "High season blocked, so you don’t approve what can’t be covered." },
      { sector: "servicios", texto: "The team calendar, to share out the work among whoever is in." },
    ],
    preguntas: [
      { pregunta: "Can I have my own absence types?", respuesta: "Yes: holidays, personal days, sick leave and any you define." },
      { pregunta: "Who approves?", respuesta: "Whoever has permission to manage absences in your company." },
    ],
  },
  {
    clave: "nominas",
    slug: "payroll",
    nombre: "Payroll",
    resumen: "Each month’s payslips delivered to each person, and the cost of your workforce.",
    titular: "Every payslip in its place and your staff costs in view.",
    entradilla:
      "You upload the month’s payslips in one go and Zindra delivers them to each person, who has it on their phone. You see what your workforce costs, final settlements included.",
    situaciones: [
      { antes: "Sending payslips one by one", ahora: "All uploaded at once and delivered", texto: "AI recognises whose each one is." },
      { antes: "“Can you resend my March payslip?”", ahora: "Each person has their own", texto: "In the app, whenever they need them." },
      { antes: "Staff costs, at your accountant’s", ahora: "Staff costs, in Zindra", texto: "Next to sales and expenses." },
    ],
    funciones: [
      { titulo: "Bulk upload", texto: "One file with all the payslips; each one goes to its person." },
      { titulo: "My payslips", texto: "Each employee sees and downloads their own." },
      { titulo: "Final settlements", texto: "In their place too." },
      { titulo: "Staff costs", texto: "What the team costs, month by month." },
    ],
    conexiones: [
      { modulo: "gastos", texto: "Payroll counts as a company expense." },
      { modulo: "informes", texto: "Staff costs against sales." },
      { modulo: "fichaje", texto: "The month’s hours, to hand for the calculation." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Teams that change every season, without losing a single payslip." },
      { sector: "servicios", texto: "The team’s cost next to what was invoiced." },
    ],
    preguntas: [
      { pregunta: "Does Zindra calculate payroll?", respuesta: "Your accountant or your payroll software prepares the payslips; Zindra delivers them, stores them and gives you the cost." },
      { pregunta: "Does each employee see only their own?", respuesta: "Yes. Each person sees only their own documents." },
    ],
  },
  {
    clave: "documentos",
    slug: "documents",
    nombre: "Documents",
    resumen: "Each person’s contracts and documents, by category and with signatures.",
    titular: "Each person’s paperwork, signed and in one place.",
    entradilla: "Contracts, annexes, procedures: each document on its person’s profile, by category, and with its signature.",
    situaciones: [
      { antes: "Contracts in a shared folder", ahora: "Each document on its person’s profile", texto: "By category." },
      { antes: "Print, sign, scan", ahora: "Signed in Zindra", texto: "And stored." },
      { antes: "“Can you send me my contract?”", ahora: "Each person has their own", texto: "In the app." },
    ],
    funciones: [
      { titulo: "By person and category", texto: "Contracts, training, procedures, whatever you need." },
      { titulo: "Signature", texto: "A signature is requested and recorded." },
      { titulo: "My documents", texto: "Each employee sees their own." },
    ],
    conexiones: [{ modulo: "nominas", texto: "Payslips and documents, on the same profile." }],
    sectores: [{ sector: "hosteleria", texto: "Hygiene procedures and training signed by each person." }],
    preguntas: [{ pregunta: "Can I bring my documents over from Factorial?", respuesta: "Yes, the Factorial connector brings the documents over if you have this module." }],
  },
  {
    clave: "tareas",
    slug: "checklists",
    nombre: "Checklists",
    resumen: "Opening, closing and cleaning lists by shift, with who did them.",
    titular: "Opening, closing and cleaning, done and signed off.",
    entradilla: "Checklists by shift or by person, ticked off from the phone. You know what was done, who did it and when, and you export it for HACCP.",
    situaciones: [
      { antes: "The list stuck on the fridge", ahora: "The list on the shift’s phone", texto: "Each item ticked with its time." },
      { antes: "“Has anyone cleaned the cold room?”", ahora: "Who did it and when", texto: "Without asking." },
      { antes: "Preparing HACCP records for an inspection", ahora: "It is exported", texto: "With each day’s record." },
    ],
    funciones: [
      { titulo: "Templates", texto: "Your usual lists, defined once." },
      { titulo: "By shift or by person", texto: "With Shifts, to the shift; without it, to people and departments." },
      { titulo: "Tags", texto: "To sort by area or type." },
      { titulo: "HACCP export", texto: "The record, ready for whoever asks for it." },
    ],
    conexiones: [{ modulo: "turnos", texto: "Tasks are assigned to the right shift." }],
    sectores: [
      { sector: "hosteleria", texto: "HACCP, temperatures and cleaning, with their record." },
      { sector: "tienda", texto: "Opening and closing of each shop." },
    ],
    preguntas: [{ pregunta: "Does it work without Shifts?", respuesta: "Yes. Without Shifts, tasks are assigned to people or departments." }],
  },
  {
    clave: "sugerencias",
    slug: "communication",
    nombre: "Communication",
    resumen: "Company news and a suggestion box.",
    titular: "What the whole team needs to know, and what the team wants to say.",
    entradilla: "Post news for the whole company and open a suggestion box, anonymous too, that reaches whoever needs to read it.",
    situaciones: [
      { antes: "The notice nobody read", ahora: "News in everyone’s app", texto: "For the whole company or a department." },
      { antes: "Complaints in the corridor", ahora: "A suggestion box", texto: "That reaches whoever decides." },
      { antes: "“Nobody told me”", ahora: "It stays published", texto: "With its date." },
    ],
    funciones: [
      { titulo: "News", texto: "Posts for the whole company, in the app." },
      { titulo: "Suggestion box", texto: "Ideas and complaints from the team, with their reply." },
    ],
    conexiones: [{ modulo: "chat", texto: "For day-to-day, Chat; for what needs to stay on record, Communication." }],
    sectores: [{ sector: "hosteleria", texto: "Notices for people without a computer, on their phone." }],
    preguntas: [{ pregunta: "Can suggestions be anonymous?", respuesta: "Yes, the person writing chooses." }],
  },
  {
    clave: "workforce",
    slug: "workforce",
    nombre: "Workforce",
    resumen: "Jobs, workstations and output per hour, with a kiosk on the floor.",
    titular: "What each person does in their hours, and the output of each one.",
    entradilla: "Each person marks which job they are on from the kiosk on the floor. You see performance by workstation and by hour, and the day’s rota.",
    requiere: "fichaje",
    situaciones: [
      { antes: "Knowing how many people are in, not what they do", ahora: "Who is on each job", texto: "From the kiosk on the floor." },
      { antes: "Productivity, by eye", ahora: "Units per hour and per person", texto: "With what comes from production and purchasing." },
      { antes: "Assigning workstations on a whiteboard", ahora: "The day’s rota", texto: "By workstation and job." },
    ],
    funciones: [
      { titulo: "Floor kiosk", texto: "A tablet to mark which job you are on." },
      { titulo: "Job catalogue", texto: "Picking, packing, shipping: the work that gets done." },
      { titulo: "Performance", texto: "Units per hour, by workstation and person." },
      { titulo: "Rota", texto: "Who goes to each workstation each day." },
    ],
    conexiones: [
      { modulo: "fichaje", texto: "Workforce starts from the clocked hours." },
      { modulo: "producciones", texto: "Production stages use its job catalogue." },
      { modulo: "compras", texto: "Expected units come from the documents." },
    ],
    sectores: [{ sector: "ecommerce", texto: "Order picking and shipping measured per hour." }],
    preguntas: [{ pregunta: "Does it need Time tracking?", respuesta: "Yes, Workforce splits the clocked hours across jobs." }],
  },
  {
    clave: "teletrabajo",
    slug: "remote-work",
    nombre: "Remote work",
    resumen: "Real activity when working remotely (time, applications and websites), tied to time tracking.",
    titular: "Remote work recorded, only while people are working.",
    entradilla: "While someone is clocked in remotely, the desktop app records their active time, applications and websites. Outside working hours, nothing.",
    situaciones: [
      { antes: "Remote work, in the dark", ahora: "The active time of each working day", texto: "Tied to time tracking." },
      { antes: "Asking for screenshots or reports by hand", ahora: "The applications and websites used", texto: "In time blocks." },
      { antes: "Monitoring outside working hours", ahora: "Only within working hours", texto: "On clocking out, it stops recording." },
    ],
    funciones: [
      { titulo: "Desktop app", texto: "Zindra Work for Mac records while you are clocked in." },
      { titulo: "Active time", texto: "By day and person." },
      { titulo: "Applications and websites", texto: "What was worked on, in time blocks." },
    ],
    conexiones: [{ modulo: "fichaje", texto: "It only records while the person is clocked in remotely." }],
    sectores: [
      { sector: "saas", texto: "Remote teams with their working time recorded." },
      { sector: "servicios", texto: "Real hours per person in an agency or advisory firm." },
    ],
    preguntas: [
      { pregunta: "Does it record outside working hours?", respuesta: "No. Only while the person is clocked in remotely." },
      { pregunta: "Which computers does it work on?", respuesta: "With the Zindra Work app for Mac." },
    ],
  },

  // ── Operations ──────────────────────────────────────────────────────────
  {
    clave: "inventario",
    slug: "inventory",
    nombre: "Inventory",
    resumen: "Stock by warehouse, with movements, counts, waste, batches and expiry dates.",
    titular: "The stock Zindra shows is the stock in the warehouse.",
    entradilla: "Every stock movement in and out is recorded: what you buy, what you produce and what you sell. With counts, waste, batches, expiry dates and alerts.",
    situaciones: [
      { antes: "The stock count that never adds up", ahora: "Every movement, recorded", texto: "Purchasing, production and sales move stock on their own." },
      { antes: "Finding out you’re out of stock while serving", ahora: "Minimum-level alerts", texto: "Before you run out." },
      { antes: "“Which batch was it from?”", ahora: "Traceability", texto: "Batches and expiry dates, from purchase to sale." },
    ],
    funciones: [
      { titulo: "Warehouses", texto: "Stock by warehouse and by location." },
      { titulo: "Counts and waste", texto: "With the difference and its reason, from the phone too." },
      { titulo: "Batches and expiry dates", texto: "With full traceability." },
      { titulo: "Codes and labels", texto: "Barcodes to count and move stock faster." },
    ],
    conexiones: [
      { modulo: "compras", texto: "What is received goes into stock." },
      { modulo: "producciones", texto: "What is consumed goes out; what is produced comes in." },
      { modulo: "facturacion", texto: "What is invoiced is deducted." },
      { modulo: "tickets", texto: "What is sold at the till is deducted." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Cold rooms, bar and storeroom, with waste and expiry dates." },
      { sector: "ecommerce", texto: "The same stock for Shopify, the warehouse and wholesalers." },
      { sector: "tienda", texto: "Stock by shop and transfers between them." },
    ],
    preguntas: [
      { pregunta: "Does it connect to Shopify?", respuesta: "Yes. Shopify and ShipHero write to Zindra’s stock." },
      { pregunta: "Can I count from my phone?", respuesta: "Yes, stock counts are done from the app." },
    ],
  },
  {
    clave: "compras",
    slug: "purchasing",
    nombre: "Purchasing",
    resumen: "Supplier orders, goods received and the price history of each item.",
    titular: "What you order, what arrives and what it really costs you.",
    entradilla: "Purchase orders, supplier delivery notes and goods received against what was ordered. Each purchase records its price, and that price feeds into the cost of what you produce.",
    situaciones: [
      { antes: "Ordering from the supplier on WhatsApp", ahora: "Purchase orders", texto: "With what was ordered, from whom and for when." },
      { antes: "Accepting the delivery note without checking", ahora: "Receiving against the order", texto: "Differences, in view." },
      { antes: "Not knowing how much the price went up", ahora: "Price history", texto: "For each item and supplier." },
    ],
    funciones: [
      { titulo: "Purchase orders", texto: "To each supplier, with their status." },
      { titulo: "Delivery notes and receiving", texto: "What was received against what was ordered." },
      { titulo: "Price history", texto: "By item and supplier." },
      { titulo: "Purchase optimisation", texto: "What to order based on what is consumed." },
    ],
    conexiones: [
      { modulo: "inventario", texto: "Goods received go into stock." },
      { modulo: "gastos", texto: "The supplier invoice is matched with its delivery note." },
      { modulo: "producciones", texto: "The latest price sets the cost of each recipe." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Daily suppliers, with the cost of each dish kept up to date." },
      { sector: "ecommerce", texto: "Raw material purchases with their shipping and duties." },
    ],
    preguntas: [{ pregunta: "Do I need Inventory?", respuesta: "No. Without Inventory, Purchasing works the same and does not move stock." }],
  },
  {
    clave: "producciones",
    slug: "production",
    nombre: "Production",
    resumen: "Recipes, recipe costing and production orders with their real cost.",
    titular: "What everything you produce really costs.",
    entradilla: "Recipes and their costing at the latest purchase price, production orders by stage and a calendar of what needs producing. The cost works itself out.",
    situaciones: [
      { antes: "Recipe costs from two years ago", ahora: "The cost at the latest purchase price", texto: "Updated every time you buy." },
      { antes: "Producing from memory", ahora: "Production orders", texto: "With their stages, quantities and batches." },
      { antes: "Selling below cost without knowing it", ahora: "The margin of each product", texto: "With the real cost." },
    ],
    funciones: [
      { titulo: "Recipes and costing", texto: "With base preparations, waste and the cost per unit." },
      { titulo: "Orders and stages", texto: "Production routes with their stages and equipment." },
      { titulo: "Calendar", texto: "What is produced each day." },
      { titulo: "Batch labels", texto: "For traceability." },
    ],
    conexiones: [
      { modulo: "compras", texto: "The latest purchase price sets the cost." },
      { modulo: "inventario", texto: "Consumes ingredients and adds what is produced to stock." },
      { modulo: "workforce", texto: "Stages use its job catalogue." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Recipes, preparations and the cost of each dish." },
      { sector: "ecommerce", texto: "Batches and orders, with the cost of each product." },
    ],
    preguntas: [{ pregunta: "Does it connect to NetSuite?", respuesta: "Yes: recipe costing, production orders and purchase costs." }],
  },
  {
    clave: "gastos",
    slug: "expenses",
    nombre: "Expenses",
    resumen: "Supplier invoices and operating expenses, matched with delivery notes.",
    titular: "Supplier invoices, read and matched.",
    entradilla: "You upload the invoice and AI extracts its details. It is matched with its delivery note, filed under its category and ready for the bank.",
    situaciones: [
      { antes: "Typing in every invoice", ahora: "AI reads the invoice", texto: "Supplier, amounts and taxes." },
      { antes: "Paying for what never arrived", ahora: "Invoice against delivery note", texto: "Differences, flagged." },
      { antes: "Fixed costs, in a notebook", ahora: "Operating expenses", texto: "Rent, utilities, fees." },
    ],
    funciones: [
      { titulo: "Supplier invoices", texto: "With AI data extraction." },
      { titulo: "Operating expenses", texto: "Whatever isn’t a purchase of goods." },
      { titulo: "Categories", texto: "To know where the money goes." },
      { titulo: "Matching with delivery notes", texto: "With Purchasing." },
    ],
    conexiones: [
      { modulo: "compras", texto: "Each invoice, with its delivery note." },
      { modulo: "banca", texto: "Each payment, with its bank transaction." },
      { modulo: "nominas", texto: "Payroll as an expense." },
    ],
    sectores: [{ sector: "hosteleria", texto: "Delivery platform fees, as an expense." }],
    preguntas: [{ pregunta: "How do invoices come in?", respuesta: "You upload them and Zindra extracts their details." }],
  },
  {
    clave: "banca",
    slug: "banking",
    nombre: "Banking",
    resumen: "Bank transactions, reconciled with invoices, expenses and the till.",
    titular: "Every bank transaction, with its invoice.",
    entradilla: "You connect your bank or import a Norma 43 file (the standard statement format of Spanish banks), and Zindra reconciles each transaction with whatever you have active: payments in, payments out and sales.",
    situaciones: [
      { antes: "Downloading the statement every month", ahora: "Your bank, connected", texto: "With GoCardless or Norma 43." },
      { antes: "Ticking off by hand", ahora: "Reconciliation", texto: "Transactions against invoices, expenses and the till." },
      { antes: "“Did that customer pay us?”", ahora: "The payment, reconciled", texto: "On the invoice." },
    ],
    funciones: [
      { titulo: "My banks", texto: "All your accounts in one place." },
      { titulo: "Norma 43 and GoCardless", texto: "File import or direct connection." },
      { titulo: "Reconciliation", texto: "Against whatever you have active." },
    ],
    conexiones: [
      { modulo: "facturacion", texto: "Customer payments." },
      { modulo: "gastos", texto: "Supplier payments." },
      { modulo: "tickets", texto: "Point-of-sale sales." },
    ],
    sectores: [{ sector: "tienda", texto: "The till close, matched with the bank." }],
    preguntas: [{ pregunta: "Is it useful without other modules?", respuesta: "Yes, it shows your statements. With Invoicing or Expenses, it reconciles." }],
  },

  // ── Sales ───────────────────────────────────────────────────────────────
  {
    clave: "facturacion",
    slug: "invoicing",
    nombre: "Invoicing",
    resumen: "Invoices, quotes, delivery notes and recurring invoices, with a link for the customer.",
    titular: "Invoices, quotes and delivery notes, in a minute.",
    entradilla: "With your numbering series, your price lists and your PDF. The customer gets a link, reminders go out on their own and recurring invoices are generated every month.",
    situaciones: [
      { antes: "The Word template", ahora: "Invoices with your numbering series", texto: "And your PDF design." },
      { antes: "Chasing payments", ahora: "Automatic reminders", texto: "And overdue invoices, flagged." },
      { antes: "Making the same invoice every month", ahora: "Recurring invoices", texto: "Generated on their own." },
    ],
    funciones: [
      { titulo: "Invoices and quotes", texto: "With their own link for the customer." },
      { titulo: "Customer delivery notes", texto: "With their preview and images." },
      { titulo: "Price lists", texto: "Prices by customer or channel." },
      { titulo: "Sales by channel", texto: "How much each channel sells." },
    ],
    conexiones: [
      { modulo: "inventario", texto: "What is invoiced is deducted from stock." },
      { modulo: "banca", texto: "Each payment, reconciled." },
      { modulo: "tickets", texto: "The till summary is an invoicing document." },
    ],
    sectores: [
      { sector: "servicios", texto: "Quotes and recurring invoices to clients." },
      { sector: "ecommerce", texto: "Wholesale and distribution, with their price lists." },
    ],
    preguntas: [{ pregunta: "Does it connect to Holded or Shopify?", respuesta: "Yes, both write to Invoicing." }],
  },
  {
    clave: "tickets",
    slug: "point-of-sale",
    nombre: "Till & receipts",
    resumen: "Point-of-sale receipts, the daily summary and the till close.",
    titular: "The till close, balanced and paper-free.",
    entradilla: "Point-of-sale receipts come in on their own, each day gets its summary and the till close is balanced by payment method.",
    requiere: "facturacion",
    situaciones: [
      { antes: "Cashing up on a piece of paper", ahora: "Till close in Zindra", texto: "By payment method." },
      { antes: "Copying point-of-sale sales by hand", ahora: "Receipts come in on their own", texto: "With Square." },
      { antes: "The day’s summary, the next day", ahora: "The daily summary", texto: "As a tax document." },
    ],
    funciones: [
      { titulo: "Receipts", texto: "Every point-of-sale sale." },
      { titulo: "Daily summary", texto: "With its numbering series and taxes." },
      { titulo: "Till close", texto: "What is there against what should be there." },
    ],
    conexiones: [
      { modulo: "inventario", texto: "What is sold is deducted from stock." },
      { modulo: "cartas_pedidos", texto: "Table payments reach the till." },
      { modulo: "banca", texto: "Sales, reconciled with the bank." },
    ],
    sectores: [
      { sector: "hosteleria", texto: "Close of each service and each venue." },
      { sector: "tienda", texto: "Each shop’s till, balanced every night." },
    ],
    preguntas: [{ pregunta: "Why does it need Invoicing?", respuesta: "The till summary is a tax document, with the numbering series and taxes from Invoicing." }],
  },
  {
    clave: "cartas_pedidos",
    slug: "table-ordering",
    nombre: "Table ordering",
    resumen: "Digital menus, table ordering, kitchen and bar screens, and delivery.",
    titular: "From the order at the table to the kitchen screen, without paper tickets.",
    entradilla: "Your digital menu with a QR code, table ordering, screens for the kitchen and the bar, and delivery orders in the same place.",
    situaciones: [
      { antes: "Handwritten order tickets", ahora: "Orders at the table", texto: "From the menu or the waiter." },
      { antes: "Shouting in the kitchen", ahora: "Kitchen and bar screens", texto: "Each dish, in its place." },
      { antes: "Three delivery tablets", ahora: "Delivery, with everything else", texto: "Glovo on the same screens." },
    ],
    funciones: [
      { titulo: "Menus", texto: "With a QR code for each table." },
      { titulo: "Orders and tables", texto: "The status of each table." },
      { titulo: "Kitchen, bar and kanban", texto: "Screens for each area." },
      { titulo: "Delivery", texto: "Orders from delivery platforms." },
    ],
    conexiones: [
      { modulo: "tickets", texto: "The table’s payment reaches the till." },
      { modulo: "producciones", texto: "Recipes, on the menu." },
      { modulo: "reservas", texto: "How full the dining room is." },
    ],
    sectores: [{ sector: "hosteleria", texto: "Bars, restaurants and cafés, with one or several dining rooms." }],
    preguntas: [{ pregunta: "Is the digital menu free?", respuesta: "The menu and the listing in the restaurant guide are free; orders and screens are this module." }],
  },
  {
    clave: "reservas",
    slug: "reservations",
    nombre: "Reservations",
    resumen: "Table bookings from your menu, with reminders.",
    titular: "Bookings from your menu, with reminders and no notebook.",
    entradilla: "The customer books from your digital menu, gets a reminder and can manage their booking. You see how full each service is.",
    situaciones: [
      { antes: "The notebook by the phone", ahora: "Bookings in Zindra", texto: "From the menu or by hand." },
      { antes: "Empty tables from no-shows", ahora: "Reminders", texto: "Before each booking." },
      { antes: "“Do you have room for us tonight?”", ahora: "How full the service is", texto: "At a glance." },
    ],
    funciones: [
      { titulo: "Bookings", texto: "With opening times and capacity." },
      { titulo: "Reminders", texto: "Sent automatically to the customer." },
      { titulo: "Managed by the customer", texto: "A link to view or change their booking." },
    ],
    conexiones: [{ modulo: "cartas_pedidos", texto: "How full the dining room is." }],
    sectores: [{ sector: "hosteleria", texto: "Restaurants with lunch and dinner services." }],
    preguntas: [{ pregunta: "Do I need Table ordering?", respuesta: "No, but together you see how full the dining room is." }],
  },

  // ── Management ──────────────────────────────────────────────────────────
  {
    clave: "informes",
    slug: "reports",
    nombre: "Reports",
    resumen: "Sales, margins and team in one dashboard.",
    titular: "How your business is doing, without building a spreadsheet.",
    entradilla: "Business and team reports with whatever you have active: sales, margins by channel, staff costs, hours. And the ones you use often, saved.",
    situaciones: [
      { antes: "Exporting from three places", ahora: "One dashboard with everything", texto: "From the modules you have." },
      { antes: "The margin, at the end of the quarter", ahora: "Margin by channel", texto: "With the real cost." },
      { antes: "Repeating the same report every Monday", ahora: "Saved reports", texto: "Ready when you open them." },
    ],
    funciones: [
      { titulo: "Business reports", texto: "Sales, expenses and results." },
      { titulo: "Team reports", texto: "Hours, absences and cost." },
      { titulo: "Analytics", texto: "By location, channel and period." },
      { titulo: "Saved", texto: "Yours, one click away." },
    ],
    conexiones: [
      { modulo: "facturacion", texto: "Sales and payments received." },
      { modulo: "fichaje", texto: "Hours worked." },
      { modulo: "producciones", texto: "Cost and margin of each product." },
    ],
    sectores: [{ sector: "ecommerce", texto: "Margin by channel: online, marketplaces, distribution." }],
    preguntas: [{ pregunta: "Which reports are available?", respuesta: "Those for the modules you have active: each block needs its source." }],
  },
  {
    clave: "chat",
    slug: "chat",
    nombre: "Chat",
    resumen: "Channels and direct messages, with threads, mentions, phone notifications and agents.",
    titular: "Your team’s chat, with agents that know your company.",
    entradilla: "Channels and direct messages for the whole team, including people without a computer. And agents you can ask about hours, holidays or sales, which look with your permissions.",
    situaciones: [
      { antes: "The company WhatsApp group", ahora: "Channels by team or project", texto: "With threads and mentions." },
      { antes: "Looking for the figure in three places", ahora: "You ask the agent", texto: "With your data and your permissions." },
      { antes: "Calls with no minutes", ahora: "Calls with notes", texto: "Inside the channel." },
    ],
    funciones: [
      { titulo: "Channels and direct messages", texto: "With threads, mentions and reactions." },
      { titulo: "Agents", texto: "Questions about your company, answered with your data." },
      { titulo: "Calls", texto: "With notes saved in the channel." },
      { titulo: "Everywhere", texto: "Web, Mac, iPhone and Android, with notifications." },
    ],
    conexiones: [
      { modulo: "proyectos", texto: "Projects live inside the chat." },
      { modulo: "informes", texto: "The agent answers with data from the active modules." },
    ],
    sectores: [
      { sector: "servicios", texto: "One channel per client, with the project alongside." },
      { sector: "saas", texto: "The remote team, in a single app." },
    ],
    preguntas: [{ pregunta: "What can the agent see?", respuesta: "What the person asking can see, never more, and only from active modules." }],
  },
  {
    clave: "proyectos",
    slug: "projects",
    nombre: "Projects",
    resumen: "Projects with tasks and subtasks, as a list, board and calendar.",
    titular: "Projects and tasks, in the same place your team talks.",
    entradilla: "Projects with tasks and subtasks as a list, board or calendar, with their columns and statuses. And if you’re coming from Asana, they come over on their own.",
    situaciones: [
      { antes: "Tasks in the chat that get lost", ahora: "Each task in its project", texto: "With an owner and a date." },
      { antes: "One more tool to pay for", ahora: "Inside Zindra Work", texto: "With the chat alongside." },
      { antes: "Migrating from Asana by hand", ahora: "Asana import", texto: "Tasks, attachments, comments and statuses." },
    ],
    funciones: [
      { titulo: "List, board and calendar", texto: "The same task, however you want to see it." },
      { titulo: "Columns and statuses", texto: "Your own." },
      { titulo: "Subtasks and attachments", texto: "With comments." },
      { titulo: "Asana import", texto: "And new items keep coming in while you use both." },
    ],
    conexiones: [{ modulo: "chat", texto: "Projects open inside the chat." }],
    sectores: [
      { sector: "servicios", texto: "Projects by client, with their deliverables." },
      { sector: "saas", texto: "Sprints on the board." },
    ],
    preguntas: [{ pregunta: "Can I bring my projects over from Asana?", respuesta: "Yes, with their tasks, attachments, comments and statuses." }],
  },
]
